import { isFulfilled } from "@reduxjs/toolkit";
import React, { useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import { CONTRACT_TEMPLATE_ID } from "../../../constants/app";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { useReopenedStages } from "../../../hooks/useReopenedStages";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import {
  checkDocStatus,
  getContractForm,
} from "../../../store/onboarding/thunk";
import { isSuperAdmin } from "../../../utils/roleHelper";
import CustomButton from "../../UI/Buttons/CustomButton";
import Download from "../../UI/Icons/Download";
import RightArrow from "../../UI/Icons/RightArrow";
import Warning from "../../UI/Icons/Warning";

/**
 * `ContractForm` is a React functional component that manages the contract review and signing process
 * during onboarding. It allows users to download and review a contract, and then proceed to the next
 * step after verifying the document signing status.
 *
 * ## Features
 * - Fetches and displays a contract form for the user to review.
 * - Provides a button to download the contract PDF.
 * - Embeds the contract form in an iframe for in-app viewing.
 * - Checks if the user has signed the contract before allowing them to proceed.
 * - Shows notifications and loading indicators during asynchronous operations.
 *
 * ## Usage
 * This component is intended to be used as a step in an onboarding flow where users are required
 * to review and sign a contract before proceeding.
 *
 * @component
 * @returns {JSX.Element} The rendered contract form step.
 */
const ContractForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const contractFormUrl = useAppSelector(
    (state) => state.onboarding.contract_form_url
  );
  const contractFormSignedUrl = useAppSelector(
    (state) => state.onboarding.contract_form_signed_url
  );
  const user = useAppSelector((state) => state.auth.user);
  const [FlkPdfUrl, setFlkPdfUrl] = React.useState<string>("");
  const [isCandidateSigned, setIsCandidateSigned] = React.useState<boolean>(false);
  const {isImpersonating} = useImpersonation();
  const superAdminCandidateData = useAppSelector(
        (state) => state.superadmin.reviewCandidateData);
  const isUserSuperAdmin = isSuperAdmin(user);
  const reviewCandidateData = useAppSelector((state) => state.superadmin.reviewCandidateData)

  const { isStageReopenedFn, reopenedSections } = useReopenedStages();
  const isStage3Reopen = isStageReopenedFn(3);

  const stage3Keys = [
    "tfn_form",
    "superannuation_form",
    "contract_form",
    "casual_fair_work_statement",
    "bankAccount",
    "emergency_info",
  ];
  const sectionRouteMap: Record<string, string> = {
    tfn_form: "tfn-declaration",
    superannuation_form: "superannuation",
    contract_form: "contract-form",
    casual_fair_work_statement: "casual-and-fair-work-statement",
    bankAccount: "bank-account",
    emergency_info: "next-of-kin",
  };

  /**
   * Handles the contract form submission process.
   *
   * This callback dispatches an action to show a transparent preloader,
   * then asynchronously fetches the contract form data by dispatching
   * the `getContractForm` action, and finally hides the preloader.
   *
   * @returns {Promise<void>} A promise that resolves when the contract form
   *          has been fetched and the preloader has been hidden.
   *
   * @remarks
   * This function is memoized using `React.useCallback` and depends on the `dispatch` function.
   */
  const handleContractForm = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(getContractForm());
    dispatch(hideTransparentPreloaderAction());
  }, [dispatch]);

  /**
   * Checks the document signing status for the current user and updates the PDF URL state if the document is signed.
   *
   * Dispatches an action to check the document status using the user's ID and a contract template ID.
   * If the document is signed (status code 200 and a valid PDF URL is returned), updates the state with the PDF URL.
   * Otherwise, dispatches a notification prompting the user to sign the document before proceeding.
   *
   * @returns {Promise<void>} A promise that resolves when the document status check and any necessary state updates or notifications are complete.
   */
  const handleDocStatus = async (): Promise<void> => {
    dispatch(showTransparentPreloaderAction());
    const docStatusPayload = {
      candidate_id: JSON.stringify(user?.id),
      template_id: CONTRACT_TEMPLATE_ID,
    };
    const docStatusResponse = await dispatch(checkDocStatus(docStatusPayload));
    if (isFulfilled(docStatusResponse)) {
      const docStatus = docStatusResponse.payload;
      if (docStatus?.data.statusCode === 200 && docStatus?.data.data.candidate_signed === true) {        
        setIsCandidateSigned(true);
        handleNext();
      } else {
        dispatch(
          showNotificationAction(
            "Please sign the document before proceeding to the next step."
          )
        );
      }
    }
    dispatch(hideTransparentPreloaderAction());
  };

  useEffect(() => {
    handleContractForm();
  }, [handleContractForm]);

  useEffect(() => {
    contractFormUrl?.length > 0 &&
      document.querySelector(".flk-form")?.classList.remove("max-w-960px");
      document.querySelector(".flk-form")?.classList.add("max-w-1200px");

      if(FlkPdfUrl?.length > 0) {
        document.querySelector(".contract-form-wrapper")?.classList.add("d-none");
      }
      
      
  }, [contractFormUrl, FlkPdfUrl]);

  useEffect(() => {
    contractFormSignedUrl && contractFormSignedUrl?.length > 0 && setFlkPdfUrl(contractFormSignedUrl);
  }, [contractFormSignedUrl]);

  /**
   * Handles the download of the contract PDF file.
   *
   * This function creates a temporary anchor element, sets its `href` attribute to the provided PDF URL,
   * and triggers a download by simulating a click on the anchor element. After the download is initiated,
   * the anchor element is removed from the DOM.
   *
   * @param {string} pdfUrl - The URL of the PDF file to be downloaded.
   */
    const handleDownloadPdf = (pdfUrl: string) => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.target = "_blank";
    link.setAttribute("download", "Contract_Form.pdf"); // Ensure the download attribute is set
    document.body.appendChild(link); // Append the link to the DOM
    link.click(); // Trigger the download
    document.body.removeChild(link); // Clean up by removing the link
  };

  const handleNext = () => {
    if (isStage3Reopen && reopenedSections.length > 0) {
      const currentIndex = stage3Keys.indexOf("contract_form");
      const stage3ReopenedSections = reopenedSections.filter(
        (key) => stage3Keys.includes(key)
      );
      const nextSection = stage3ReopenedSections.find(
        (key) => stage3Keys.indexOf(key) > currentIndex
      );
      if (nextSection) {
        navigate(
          isUserSuperAdmin
            ? `/admin/candidate-profile/${sectionRouteMap[nextSection]}`
            : `/candidate/my-profile/${sectionRouteMap[nextSection]}`
        );
        return;
      }
    }
    navigate(isUserSuperAdmin ? "/admin/candidate-profile/casual-and-fair-work-statement" : "/candidate/my-profile/casual-and-fair-work-statement");
  };

  useEffect(() => {
    if(isUserSuperAdmin){
      const flkDocument = reviewCandidateData?.flk_documents?.find((doc => doc.is_active_contract_form === "1"));
      if(flkDocument){
        setFlkPdfUrl(flkDocument.s3_pdf_url);
      }
    }
  }, [reviewCandidateData]);

  return (
    <div className="d-flex flex-column gap-20">
      {/* <div className="fs-30 fw-400 text-black">Review Contract</div>
      <div className="d-flex flex-column gap-10">
        <div className="fs-16 fw-400 text-black">
          Before you begin filling in the documents, please download and read
          the below contract that you will be required to sign in Step 3 of this
          stage.
        </div>
        <div className="d-flex">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-off-white text-black"
            onClick={openContractForm}
          >
            <Download />
            Contract.pdf
          </CustomButton>
        </div>
      </div>
      <div className="h-10px"></div> */}
      <div className="fs-30 fw-400 text-black">Contract Declaration</div>
      { isImpersonating && 
        <div className={`bg-teal-light px-15px py-10px radius-8px d-flex gap-6`}>
          <Warning />
          <div className="fs-13 fw-400 text-black lh-sm">
            You are currently impersonating a candidate. You cannot sign the document while in impersonation mode.
          </div>
        </div>
      }
      <div className="contract-form-wrapper">
        {contractFormUrl?.length > 0 ? (
          <>

          {
            !isImpersonating  &&
             <iframe
              src={contractFormUrl}
              title="flk-client"
              className="flk-client flk-iframe"
              width={100}
            ></iframe>
          }
           

            <div className="button-group justify-content-between mt-2">
              <div></div>
              {!isImpersonating && (
                <CustomButton
                  type="button"
                  variant="primary"
                  className={`btn-theme bg-teal text-black`}
                  onClick={handleDocStatus}
                >
                Next <RightArrow color={"#000000"} />
              </CustomButton>
              )}
            </div>
          </>
        ) : (
          ""
        )}
        </div>
        <div>
          {FlkPdfUrl?.length > 0 && (
            <div className="d-flex flex-column gap-20">
              <div className="fw-400 fs-16">Please click the link below to download a copy of this document for your records.</div>
              <div className="d-flex">
                <CustomButton
                  type="button"
                  className="btn-theme bg-teal text-white"
                  variant="primary"
                  onClick={() => handleDownloadPdf(FlkPdfUrl)}
                >
                  <Download />
                  <div className="text-black">Download Contract Declaration PDF</div>
                </CustomButton>
              </div>
              <div className="d-flex">
                <CustomButton
                  type="submit"
                  variant="primary"
                  onClick={handleNext}
                  className="btn-theme bg-teal text-black  mt-20"
                >
                  Next
                  <RightArrow color={"#000000"} />
                </CustomButton>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default ContractForm;
